<template>
  <div v-if="!readOnly && !field.readOnly">
    <v-text-field :ref="inputRef"
                  v-model="fieldValue"
                  :prefix="field.prefix"
                  :counter="maxLength"
                  :maxlength="maxLength"
                  :suffix="field.suffix"
                  :multi-line="field.multiLine"
                  :hint="field.hint"
                  :persistent-hint="true"
                  :disabled="field.disabled"
                  :color="field.color"
                  :rules="passwordValidationRules"
                  :label="label"
                  type="password"
                  :class="field.class"
                  :prepend-icon="field.prependIcon"
                  :autofocus="field.autofocus"
                  :filled="box || field.box"
                  :autocomplete="field.disableAutoComplete ? 'off' : undefined"
                  :clearable="field.clearable"
                  :required="maybeCall(field.required, value)"
                  @input="onInputPassword"
                  @keydown.enter="handleEnter"
                  @blur="onBlurTextField" />
    <v-row justify="center">
      <v-chip small color="white" disabled>
        <v-avatar>
          <v-icon v-if="upperOk" small color="success">
            check
          </v-icon>
        </v-avatar>
        Maiúsculas
      </v-chip>
      <v-chip small color="white" disabled>
        <v-avatar>
          <v-icon v-if="lowerOk" small color="success">
            check
          </v-icon>
        </v-avatar>
        Minúsculas
      </v-chip>
      <v-chip small color="white" disabled>
        <v-avatar>
          <v-icon v-if="numberOk" small color="success">
            check
          </v-icon>
        </v-avatar>
        Números
      </v-chip>
      <v-chip small color="white" disabled>
        <v-avatar>
          <v-icon v-if="symbolOk" small color="success">
            check
          </v-icon>
        </v-avatar>
        Caracteres especiais
      </v-chip>
      <v-chip small color="white" disabled>
        <v-avatar>
          <v-icon v-if="lengthOk" small color="success">
            check
          </v-icon>
        </v-avatar>
        Mais de 8 caracteres
      </v-chip>
    </v-row>
  </div>
  <div v-else :class="{'reg-information': !small, 'reg-information-small': small}">
    <label>{{ field.label }}</label>
    <p>{{ '*********' }}</p>
  </div>
</template>

<script>
import { defineComponent, ref, computed, toRef } from 'vue'
import { passwordStrength } from 'check-password-strength'
import { useDynamicField } from '../composables/useDynamicField'

export default defineComponent({
  name: 'DynamicPasswordField',
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    field: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    box: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    isValidating: {
      type: Boolean,
      default: false
    },
    itemKey: {
      type: String,
      default: ''
    }
  },
  emits: ['input', 'change'],
  setup(props, { emit, expose }) {
    const {
      fieldValue,
      label,
      inputRef,
      maybeCall,
      fieldChanged,
      hasError
    } = useDynamicField({
      value: toRef(props, 'value'),
      field: props.field,
      readOnly: props.readOnly,
      isValidating: toRef(props, 'isValidating'),
      itemKey: props.itemKey,
      emptyValue: '',
      emit
    })

    const strength = ref({})

    const lengthOk = computed(() => {
      return strength.value?.length >= 8
    })

    const upperOk = computed(() => {
      return strength.value?.contains?.includes('uppercase')
    })

    const lowerOk = computed(() => {
      return strength.value?.contains?.includes('lowercase')
    })

    const symbolOk = computed(() => {
      return strength.value?.contains?.includes('symbol')
    })

    const numberOk = computed(() => {
      return strength.value?.contains?.includes('number')
    })

    const maxLength = computed(() => {
      if (props.field.maxLength && String(props.field.maxLength) !== '0') {
        return parseInt(props.field.maxLength)
      }
      return undefined
    })

    const passwordValidationRules = computed(() => {
      return [
        v => (!!v || !props.field.required) || 'Informe a senha',
        v => (!v || strength.value?.id >= 2) || 'Senha fraca, verifique as regras de senha abaixo'
      ]
    })

    const onInputPassword = (password) => {
      strength.value = passwordStrength(password)
    }

    const handleEnter = () => {
      onBlurTextField()
    }

    const onBlurTextField = () => {
      fieldChanged(fieldValue.value)
    }

    // Expose properties for DynamicForm validation
    expose({
      hasError,
      validate: () => inputRef.value?.validate?.()
    })

    return {
      fieldValue,
      label,
      maybeCall,
      lengthOk,
      upperOk,
      lowerOk,
      symbolOk,
      numberOk,
      maxLength,
      passwordValidationRules,
      onInputPassword,
      handleEnter,
      onBlurTextField,
      inputRef
    }
  }
})
</script>

<style lang="scss">
</style>